"use client";

// Tag de Google Ads (AW-…) + captura de los click ids (gclid/gbraid/wbraid).
//
// - Captura ?gclid=/?gbraid=/?wbraid= SIEMPRE (aunque el tag no esté configurado): el
//   dato se acumula en cookie/localStorage desde el día 0 para el respaldo server-side
//   (sales.gclid/gbraid/wbraid). gbraid/wbraid = tráfico iOS/ITP, sin gclid.
// - El config del tag se apoya en el gtag.js que ya carga <GoogleAnalytics> en prod
//   (un solo download de gtag.js; los productos extra se agregan con gtag('config')).
//   Si GA4 no está activo (dev / env ausente), withLoader=true baja gtag.js con el
//   id de Ads para poder probar el flujo completo.
// - allow_enhanced_conversions: permite mandar user_data (email) con las conversiones.
// - page_location: se manda EXPLICITO con el locale adelante. El proxy hace rewrite
//   interno sin redirect, asi que el navegador muestra "/libros" y la URL recien se
//   corrige en el cliente con history.replaceState (LocaleUrlSync). Este script corre
//   afterInteractive y le gana la carrera, o sea que Google registraba la URL SIN idioma.
//   Medido el 04-ago-2026: las 7 listas de remarketing por idioma juntaban 1.026 usuarios
//   contra 8.300 de la lista general, o sea el 12%. Sin esto la segmentacion por idioma
//   de la campana de Display no funciona.
// - En el 404 de [locale] el config va sin page_view (ver lib/es404.js).

import { useEffect } from "react";
import Script from "next/script";
import { ES_404_JS } from "@/lib/es404";
import { captureClickIds } from "@/lib/gclid";
import { captureWaToken } from "@/lib/watoken";
import { captureFbclid } from "@/lib/fbclid";
import { captureAdParams } from "@/lib/adparams";
import { captureRef } from "@/lib/reflink";
import { ensureFbp } from "@/lib/fbp";
import { VISIBLES } from "@/lib/idiomas";

export default function GoogleAdsTag({ adsId, withLoader = false, locale = null }) {
  useEffect(() => {
    captureClickIds();
    // Mismo punto de captura para el token de atribución de WhatsApp (?wa=)
    captureWaToken();
    // Y para el click id de Meta (?fbclid=): no depende de este tag ni de que el
    // pixel haya cargado, por eso vive acá junto al resto de las capturas.
    captureFbclid();
    // Y los utm_* del aviso: Meta manda el id del aviso en utm_content, que es lo único
    // que permite agrupar ventas por aviso con plata nuestra. Ver lib/adparams.js.
    captureAdParams();
    // Y el código de un link de marca (?ref=), que el admin cuenta en Links de marcas.
    // Ver lib/reflink.js.
    captureRef();
    // El browser id de Meta (_fbp) se crea acá si fbevents.js no lo escribió (pixel
    // bloqueado o todavía bajando). Adelantarlo al pageload es lo que hace que el
    // primer evento ya lo lleve; fbevents adopta la cookie si ya está.
    ensureFbp();
  }, []);

  if (!adsId) return null;

  return (
    <>
      {withLoader ? (
        <Script
          src={`https://www.googletagmanager.com/gtag/js?id=${adsId}`}
          strategy="afterInteractive"
        />
      ) : null}
      <Script id="google-ads-config" strategy="afterInteractive">
        {`window.dataLayer = window.dataLayer || [];
window.gtag = window.gtag || function(){window.dataLayer.push(arguments);};
gtag('js', new Date());
gtag('config', '${adsId}', { allow_enhanced_conversions: true, send_page_view: !${ES_404_JS}${
          locale
            ? `, page_location: (function(){var p=location.pathname,s=p.split('/')[1];`
              + `return ${JSON.stringify(VISIBLES)}.indexOf(s)>=0 ? location.href`
              // La home tiene que quedar como "/pt/" CON barra final: la regla de la lista
              // busca "/pt/" y "/pt" a secas no matchea.
              + ` : location.origin+'/'+${JSON.stringify(locale)}+(p==='/'?'/':p)+location.search;})()`
            : ''
        } });`}
      </Script>
    </>
  );
}
